import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import cn from 'classnames';
import { noop } from 'lodash-es';
import { FC, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { NoteList } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import { Checkbox, Icon, Tooltip } from '@/shared/ui';

import {
  isFlexibleTemplateType,
  type DocumentSectionSpec,
} from '@/entities/templates';

import {
  answerKey,
  getOrphanSlots,
  getStructureItemId,
  hasSectionNote,
  resolveAnswer,
  type DraftAnswers,
  type SurfaceSection,
} from '../lib';

import { AiMark } from './AiMark';
import { InlineText } from './InlineText';
import { InstructionLine } from './InstructionLine';
import { RowTool } from './NoteRowTools';
import { SettingsPopover } from './SettingsPopover';
import { TemplateLineRow } from './TemplateFieldRow';
import { ReleaseButton, TemplateFieldView } from './TemplateFieldView';
import styles from './TemplateSection.module.scss';

type ShellProps = {
  sortableId: string;
  // A working copy's sections are dragged and renamed.
  editable: boolean;
  dropDisabled: boolean;
  title: string;
  startEditingTitle?: boolean;
  // Small tags after the title, e.g. "Scored 1–5".
  tags?: ReactNode;
  // Present while the template is edited: it is switched in the head's "⋯".
  required?: { value: boolean; onToggle: () => void };
  // Round controls at the end of the head: guidance, settings.
  controls?: ReactNode;
  className?: string;
  sectionCode: string;
  sectionId?: string;
  children: ReactNode;
  onTitleCommit?: (title: string) => void;
  onEditEnd?: () => void;
  onRemove?: () => void;
};

// A section in any format: title, settings and, in a working copy, its drag
// handle and delete.
export const SectionShell: FC<ShellProps> = ({
  sortableId,
  editable,
  dropDisabled,
  title,
  startEditingTitle,
  tags,
  required,
  controls,
  className,
  sectionCode,
  sectionId,
  children,
  onTitleCommit,
  onEditEnd,
  onRemove,
}) => {
  const { formatMessage } = useIntl();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId,
    disabled: { draggable: !editable, droppable: !editable || dropDisabled },
  });
  const moveLabel = formatMessage(
    {
      id: 'workspace.template.moveSection',
      defaultMessage: 'Move section “{title}”',
    },
    { title },
  );
  const deleteLabel = formatMessage({
    id: 'workspace.template.deleteSection',
    defaultMessage: 'Delete section',
  });
  const menuLabel = formatMessage({
    id: 'workspace.template.sectionSettings',
    defaultMessage: 'Section settings',
  });
  const titleLabel = formatMessage({
    id: 'workspace.template.sectionTitle',
    defaultMessage: 'Section title',
  });

  return (
    <section
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        styles.section,
        editable && styles.editable,
        isDragging && styles.dragging,
        className,
      )}
      aria-label={title}
      data-component="workspace-template-section"
      data-section-code={sectionCode}
      data-section-id={sectionId}
      // Focused when the missing-sections warning points at it.
      tabIndex={-1}
    >
      <div className={styles.head}>
        {editable && (
          <button
            type="button"
            ref={setActivatorNodeRef}
            className={styles.grip}
            {...attributes}
            {...listeners}
            aria-label={moveLabel}
            title={moveLabel}
          >
            <span className={styles.gripDots} aria-hidden />
          </button>
        )}
        <h3 className={styles.title}>
          <InlineText
            value={title}
            label={titleLabel}
            editable={editable}
            startEditing={startEditingTitle}
            onCommit={(next) => onTitleCommit?.(next)}
            onEditEnd={onEditEnd}
          />
        </h3>
        {tags}
        <span className={styles.headEnd}>
          {controls}
          {required && (
            <SettingsPopover
              label={menuLabel}
              dataComponent="workspace-template-section-menu"
            >
              <Checkbox checked={required.value} onChange={required.onToggle}>
                <FormattedMessage
                  id="workspace.template.required"
                  defaultMessage="Required section"
                />
              </Checkbox>
            </SettingsPopover>
          )}
          {editable && onRemove && (
            <RowTool
              icon="close"
              danger
              label={deleteLabel}
              className={styles.hoverAction}
              onClick={onRemove}
            />
          )}
        </span>
      </div>
      {children}
    </section>
  );
};

type Props = {
  section: DocumentSectionSpec;
  answers: DraftAnswers;
  multiSelect: Record<string, true>;
  showInstructions: boolean;
  // The note of a flexible section in a live document.
  noteMode?: 'edit' | 'view';
  className?: string;
  onAnswer: (key: string, value: string) => void;
  onRelease?: (key: string) => void;
  onAllowMultiple?: (key: string) => void;
};

// A section of an option template: fields with chips, phrases and free text.
export const TemplateSection: FC<Props> = ({
  section,
  answers,
  multiSelect,
  showInstructions,
  noteMode,
  className,
  onAnswer,
  onRelease,
  onAllowMultiple,
}) => {
  const { formatMessage } = useIntl();
  const options = section.Options;
  const flexible = isFlexibleTemplateType(section.TemplateType);
  // A section without fields keeps its text under the section key; a
  // flexible section with fields keeps its note there.
  const sectionKey = answerKey(section.Code);
  const freeText = resolveAnswer(answers, sectionKey);
  const showNote =
    !!noteMode &&
    hasSectionNote(section) &&
    (noteMode === 'edit' || !!freeText.value.trim());

  return (
    <SectionShell
      sortableId={getStructureItemId({ kind: 'section', code: section.Code })}
      editable={false}
      dropDisabled
      title={section.Title}
      className={className}
      sectionCode={section.Code}
      tags={
        options.length > 0 &&
        flexible && (
          <span className={cn('p4', styles.modeTag)}>
            <FormattedMessage
              id="workspace.template.flexibleTag"
              defaultMessage="AI may add text"
            />
          </span>
        )
      }
    >
      {showInstructions && (
        <InstructionLine
          className={styles.guide}
          value={section.Instructions ?? ''}
          label={formatMessage({
            id: 'workspace.instructions',
            defaultMessage: 'AI instruction',
          })}
          placeholder={formatMessage({
            id: 'workspace.template.addInstruction',
            defaultMessage: '+ AI instruction',
          })}
          editable={false}
          onCommit={noop}
        />
      )}
      <div className={styles.fields}>
        {options.length === 0 && (
          <div className={styles.freeTextRow}>
            <textarea
              className={cn(
                'p2',
                styles.freeText,
                freeText.source === 'ai' && styles.aiText,
              )}
              rows={1}
              aria-label={section.Title}
              placeholder={formatMessage({
                id: 'workspace.template.fillLater',
                defaultMessage: 'Filled from the recording',
              })}
              value={freeText.value}
              onChange={(event) => onAnswer(sectionKey, event.target.value)}
            />
            {freeText.source === 'ai' && freeText.value && <AiMark />}
            {onRelease && freeText.source === 'manual' && (
              <ReleaseButton onClick={() => onRelease(sectionKey)} />
            )}
          </div>
        )}
        {options.map((option, index) => (
          <TemplateFieldView
            key={`${section.Code}/${index}`}
            sectionCode={section.Code}
            sectionTitle={section.Title}
            optionIndex={index}
            option={option}
            answers={answers}
            multiSelect={multiSelect}
            showInstructions={showInstructions}
            orphanSlots={getOrphanSlots(option)}
            onAnswer={onAnswer}
            onRelease={onRelease}
            onAllowMultiple={onAllowMultiple}
          />
        ))}
        {showNote && (
          <div className={styles.note}>
            <span className={cn('p2', styles.noteLabel)}>
              <FormattedMessage
                id="workspace.live.sectionNote"
                defaultMessage="Additional text"
              />
              {freeText.source === 'ai' && freeText.value && <AiMark />}
            </span>
            {noteMode === 'edit' ? (
              <div className={styles.freeTextRow}>
                <textarea
                  className={cn(
                    'p2',
                    styles.freeText,
                    freeText.source === 'ai' && styles.aiText,
                  )}
                  rows={1}
                  aria-label={formatMessage(
                    {
                      id: 'workspace.live.sectionNoteOf',
                      defaultMessage: 'Additional text of “{title}”',
                    },
                    { title: section.Title },
                  )}
                  placeholder={formatMessage({
                    id: 'workspace.live.notePlaceholder',
                    defaultMessage: 'What the fields do not cover',
                  })}
                  value={freeText.value}
                  onChange={(event) => onAnswer(sectionKey, event.target.value)}
                />
                {onRelease && freeText.source === 'manual' && (
                  <ReleaseButton onClick={() => onRelease(sectionKey)} />
                )}
              </div>
            ) : (
              <p className={cn('p2', styles.noteText)}>{freeText.value}</p>
            )}
          </div>
        )}
      </div>
    </SectionShell>
  );
};

type LineSectionProps = {
  // The template's section: absent only for a section scribe added.
  section: DocumentSectionSpec | undefined;
  surface: SurfaceSection;
  lists: NoteList[];
  className?: string;
};

// The section's AI guidance and whether AI may reword its text blocks, as
// the template's edit mode sets them.
const GuidanceMark: FC<{ guidance: string; rewrite: boolean }> = ({
  guidance,
  rewrite,
}) => {
  const { formatMessage } = useIntl();
  const content = [
    guidance &&
      formatMessage(
        {
          id: 'workspace.blockEditor.guidanceTooltip',
          defaultMessage: 'AI guidance: {text}',
        },
        { text: guidance },
      ),
    rewrite &&
      formatMessage({
        id: 'workspace.noteEditor.sectionRewrite',
        defaultMessage: 'AI may rewrite the wording',
      }),
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Tooltip.Primary content={content}>
      <button
        type="button"
        className={cn(styles.round, styles.guidance)}
        aria-label={formatMessage({
          id: 'workspace.blockEditor.sectionSettings',
          defaultMessage: 'AI guidance and rewording for the section',
        })}
        data-component="workspace-template-section-guidance"
      >
        <Icon name="comment2Filled" size={16} />
      </button>
    </Tooltip.Primary>
  );
};

// A section of a block template read before any note exists: its lines as
// sentences. The note is filled on its own surface.
export const TemplateLineSection: FC<LineSectionProps> = ({
  section,
  surface,
  lists,
  className,
}) => {
  const title = surface.title || section?.Title || '';
  const guidance = section?.Instructions?.trim() ?? '';
  const rewrite = !!section?.AllowAITextRewrite;

  return (
    <SectionShell
      sortableId={getStructureItemId({ kind: 'section', code: surface.code })}
      editable={false}
      dropDisabled
      title={title}
      className={className}
      sectionCode={surface.code}
      sectionId={surface.id}
      controls={
        (guidance || rewrite) && (
          <GuidanceMark guidance={guidance} rewrite={rewrite} />
        )
      }
    >
      <div className={cn(styles.fields, styles.lines)}>
        {surface.lines.map((line) => (
          <TemplateLineRow key={line.id} line={line} lists={lists} />
        ))}
        {surface.hiddenLinked > 0 && (
          <p className={cn('p4', styles.emptyLines)}>
            <FormattedMessage
              id="workspace.blockEditor.hiddenLinked"
              defaultMessage="{count, plural, one {# linked line} other {# linked lines}} hidden"
              values={{ count: surface.hiddenLinked }}
            />
          </p>
        )}
      </div>
    </SectionShell>
  );
};
